import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { toast } from "sonner";
import { LogOut, Clock, FileText, Play, AlertTriangle } from "lucide-react";
import { paletteFromColor } from "../components/ClayCard";

const QUESTION_FONT = { sm: "text-xl md:text-2xl", md: "text-2xl md:text-3xl", lg: "text-3xl md:text-4xl", xl: "text-4xl md:text-5xl" };
const IMAGE_MAX = { sm: "max-h-[24vh]", md: "max-h-[36vh]", lg: "max-h-[48vh]", xl: "max-h-[60vh]" };

const clampNum = (v, min, max, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

export default function QuizPlay() {
  const { mode, quizId } = useParams();
  const navigate = useNavigate();
  const { t, lang, settings } = useApp();
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [penaltyTotal, setPenaltyTotal] = useState(0);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalTime, setTotalTime] = useState(300);
  const [remaining, setRemaining] = useState(300);
  const [timeUp, setTimeUp] = useState(false);
  const [grace, setGrace] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [wrongIdx, setWrongIdx] = useState([]); // option indices chosen wrong this question
  const [correctIdx, setCorrectIdx] = useState(null); // chosen correct option (green flash)
  const [phase, setPhase] = useState("intro"); // "password" | "intro" | "preparing" | "countdown" | "playing"
  const [quizType, setQuizType] = useState(null);
  const [pwInput, setPwInput] = useState("");
  const [pwChecking, setPwChecking] = useState(false);
  const [countdownNum, setCountdownNum] = useState(3);
  // Fairness: all question images are preloaded into the browser cache BEFORE the
  // timer starts, so every player sees each new image appear instantly (identical
  // time) regardless of their network speed, device or browser. imagesReady gates
  // the start of timing.
  const [imagesReady, setImagesReady] = useState(false);
  const [preloadPct, setPreloadPct] = useState(0);
  const [penaltyFlash, setPenaltyFlash] = useState(null); // { n, key }
  const finishedRef = useRef(false);
  const graceRef = useRef(false);
  const penaltyRef = useRef(0);
  const wrongRef = useRef(0);
  const correctRef = useRef(0);

  const L = (key) => settings[`${key}_${lang}`] || settings[`${key}_pt`] || "";

  const pwPromptKey = quizType === "competition" ? "teacher_pw_prompt_competition" : "teacher_pw_prompt_training";
  const pwPrompt = settings[`${pwPromptKey}_${lang}`] || settings[`${pwPromptKey}_pt`]
    || (lang !== "en" ? "Introduza a senha de professor por favor!" : "Please enter the teacher password!");

  const verifyTeacherPassword = async (e) => {
    if (e) e.preventDefault();
    setPwChecking(true);
    try {
      const { data } = await api.post("/quiz/verify-teacher-password", { type: quizType || "training", password: pwInput });
      if (data.ok) {
        setPhase(settings.quiz_intro_enabled === false ? "preparing" : "intro");
      } else {
        toast.error(lang !== "en" ? "Senha incorreta" : "Incorrect password");
        setPwInput("");
      }
    } catch (_) {
      toast.error(lang !== "en" ? "Erro ao verificar a senha" : "Error verifying password");
    } finally {
      setPwChecking(false);
    }
  };
  const fontSize = QUESTION_FONT[settings.quiz_question_font_size] || QUESTION_FONT.md;
  const imageMax = IMAGE_MAX[settings.quiz_image_size] || IMAGE_MAX.md;
  // Fine-grained sizing (configured in Admin → Aparência do Quiz)
  const questionPx = clampNum(settings.quiz_question_text_px, 12, 48, 22);
  const answerPx = clampNum(settings.quiz_answer_text_px, 12, 36, 16);
  const imageAspect = settings.quiz_image_aspect || "auto"; // "auto" | "1/1" | "4/3" | "16/9" | "3/4"
  const imageFit = settings.quiz_image_fit === "cover" ? "cover" : "contain";
  const fixedAspect = imageAspect !== "auto";
  // Answer button styling (configured in Admin → Aparência do Quiz, inspired by "Botões X")
  const answerFormat = settings.quiz_answer_format || "soft";
  const answerEffect = settings.quiz_answer_effect || "lift";
  const answerFont = settings.quiz_answer_font || "fredoka";
  const answerPalette = paletteFromColor(settings.quiz_answer_color || "snow");
  // 3-area layout proportions (configured in Admin → Aparência do Quiz). Total is arbitrary; fr-based.
  const colL = clampNum(settings.quiz_col_left, 1, 16, 2);
  const colC = clampNum(settings.quiz_col_center, 1, 16, 11);
  const colR = clampNum(settings.quiz_col_right, 1, 16, 5);
  const sideVCenter = settings.quiz_side_vcenter === true || settings.quiz_side_vcenter === "true";

  // Preload every question image into the browser cache. Resolves once all images
  // are fetched (or on a safety timeout) so the timer never starts before they are
  // ready — guaranteeing an identical "next image appears" moment for all players.
  const preloadImages = useCallback((qs) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const urls = (qs || [])
      .map((q) => (q.image_path ? `${backend}/api/files/${q.image_path}` : null))
      .filter(Boolean);
    if (urls.length === 0) {
      setPreloadPct(100);
      setImagesReady(true);
      return;
    }
    let done = 0;
    const bump = () => {
      done += 1;
      setPreloadPct(Math.round((done / urls.length) * 100));
      if (done >= urls.length) setImagesReady(true);
    };
    urls.forEach((u) => {
      const img = new Image();
      const finish = () => {
        // Decode ahead of time so the later paint is instant and jitter-free.
        if (img.decode) { img.decode().then(bump).catch(bump); } else { bump(); }
      };
      img.onload = finish;
      img.onerror = bump;
      img.src = u;
    });
    // Safety net: never block longer than 15s even if a file stalls.
    setTimeout(() => setImagesReady(true), 15000);
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get(`/quizzes/${quizId}`),
      api.get(`/quizzes/${quizId}/questions?active_only=true`),
    ])
      .then(([quizRes, qRes]) => {
        if (!active) return;
        const gt = parseInt(quizRes.data?.global_time_seconds, 10) || 300;
        setTotalTime(gt);
        setRemaining(gt);
        const qs = qRes.data || [];
        setQuestions(qs);
        preloadImages(qs);
        const qtype = quizRes.data?.type === "competition" ? "competition" : "training";
        setQuizType(qtype);
        const needPw = qtype === "competition"
          ? settings.teacher_pw_competition_required
          : settings.teacher_pw_training_required;
        // A teacher-password gate (if set) comes BEFORE the "Antes de começares" intro.
        if (needPw) {
          setPhase("password");
        } else if (settings.quiz_intro_enabled === false) {
          setPhase("preparing");
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error(lang !== "en" ? "Quiz não encontrado" : "Quiz not found");
        navigate("/");
      });
    return () => { active = false; };
  }, [quizId, mode, navigate, lang, preloadImages]);

  // When the intro is disabled, start timing the instant images are ready.
  useEffect(() => {
    if (phase === "preparing" && imagesReady) {
      setPhase("playing");
      setStartTime(Date.now());
    }
  }, [phase, imagesReady]);

  const finishQuiz = useCallback(
    async (timedOut = false) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      const rawResolution = (Date.now() - startTime) / 1000;
      // Tolerance rule: if completed during the grace window, cap the recorded time at the limit.
      const resolution = timedOut ? rawResolution : Math.min(rawResolution, totalTime);
      const participantId = sessionStorage.getItem("participant_id");
      const participantName = sessionStorage.getItem("participant_name");
      try {
        await api.post("/play/submit", {
          quiz_id: quizId,
          participant_id: mode === "competition" ? participantId : null,
          participant_name: mode === "training" ? (participantName || "") : null,
          resolution_seconds: resolution,
          penalty_seconds: penaltyRef.current,
          wrong_attempts: wrongRef.current,
          correct_count: correctRef.current,
          timed_out: timedOut,
        });
      } catch (e) {
        console.error(e);
      }
      sessionStorage.setItem(
        "last_result",
        JSON.stringify({
          resolution,
          penalty: penaltyRef.current,
          final: resolution + penaltyRef.current,
          wrong: wrongRef.current,
          quizId,
          mode,
          timedOut,
        })
      );
      if (!timedOut) navigate("/complete");
    },
    [startTime, quizId, mode, navigate, totalTime]
  );

  // Global countdown
  useEffect(() => {
    if (loading || startTime === null || timeUp || finishedRef.current) return;
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const left = totalTime - elapsed;
      if (left <= 0) {
        setRemaining(0);
        // Time limit reached: enter the 3s tolerance window. The quiz stays fully
        // playable (only a slight dim) and answers remain valid. If the player has
        // not finished by the end of the window, the quiz ends as "time ran out".
        if (!graceRef.current) {
          graceRef.current = true;
          setGrace(true);
          setTimeout(() => {
            if (!finishedRef.current) {
              setTimeUp(true);
              finishQuiz(true);
            }
          }, 3000);
        }
      } else {
        setRemaining(left);
      }
    };
    const interval = setInterval(tick, 200);
    return () => clearInterval(interval);
  }, [loading, startTime, totalTime, timeUp, finishQuiz]);

  // Pre-start: show 3-2-1 countdown then begin timing
  const startQuiz = () => {
    if (!imagesReady) return;
    setPhase("countdown");
    setCountdownNum(3);
  };
  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdownNum <= 0) {
      setPhase("playing");
      setStartTime(Date.now());
      // Mark the participation as actually started (competition mode only). From
      // this point onwards, leaving without submitting counts as desistência.
      if (mode === "competition") {
        const pid = sessionStorage.getItem("participant_id");
        if (pid) {
          api.post("/play/compete/begin", { participant_id: pid }).catch(() => {});
        }
      }
      return;
    }
    const tmo = setTimeout(() => setCountdownNum((n) => n - 1), 1000);
    return () => clearTimeout(tmo);
  }, [phase, countdownNum, mode]);

  // Warn the student that closing the tab / navigating away mid-quiz will be
  // registered as a withdrawal ("Desistência"). Only active once the test is
  // actually running (phase==="playing") in competition mode.
  useEffect(() => {
    if (mode !== "competition" || phase !== "playing" || finishedRef.current) return;
    const handler = (e) => {
      e.preventDefault();
      // Modern browsers ignore custom text and always show their own message,
      // but setting returnValue is required to actually trigger the prompt.
      e.returnValue = lang !== "en"
        ? "Se sair agora, a prova será registada como desistência. Tem a certeza?"
        : "If you leave now, this attempt will be recorded as a withdrawal. Are you sure?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [mode, phase, lang]);

  const onAnswer = (optIdx) => {
    if (phase !== "playing" || timeUp || correctIdx !== null) return;
    if (wrongIdx.includes(optIdx)) return;
    const q = questions[idx];
    if (optIdx === q.correct_index) {
      setCorrectIdx(optIdx);
      correctRef.current += 1;
      setTimeout(() => {
        setWrongIdx([]);
        setCorrectIdx(null);
        if (idx + 1 >= questions.length) {
          finishQuiz(false);
        } else {
          setIdx(idx + 1);
        }
      }, 650);
    } else {
      const pen = q.penalty_seconds || 10;
      penaltyRef.current += pen;
      wrongRef.current += 1;
      setPenaltyTotal((p) => p + pen);
      setWrongAttempts((w) => w + 1);
      setWrongIdx((arr) => [...arr, optIdx]);
      setPenaltyFlash({ n: pen, key: Date.now() });
      setTimeout(() => setPenaltyFlash((f) => (f && Date.now() - f.key >= 1800 ? null : f)), 1900);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-fredoka text-2xl text-[#0B1D3A]">
        {t("loading", "A carregar...")}
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="font-fredoka text-2xl text-[#0B1D3A]">
          {lang !== "en" ? "Este quiz ainda não tem perguntas." : "This quiz has no questions yet."}
        </p>
        <button onClick={() => navigate("/")} className="mt-6 px-6 py-3 rounded-full bg-[#0B1D3A] text-white font-bold" data-testid="quiz-back-btn">
          {t("back", "Voltar")}
        </button>
      </div>
    );
  }

  const q = questions[idx];
  const opts = lang === "en"
    ? q.options_en
    : (lang === "br" ? (q.options_pt || []).map((o, i) => (q.options_br && q.options_br[i]) || o) : q.options_pt);
  const questionText = lang === "en"
    ? q.question_en
    : (lang === "br" ? (q.question_br || q.question_pt) : q.question_pt);
  const introDefault = lang === "en"
    ? "Be quick, but don't rush! Your goal is to finish all the challenges as fast as you can. Before you choose an answer, take a few seconds to check that it's right. A wrong answer adds extra time, so the best way to win is to be both fast and careful!"
    : "Sê rápido, mas não te precipites! O teu objetivo é terminar todos os desafios o mais depressa possível. Antes de escolheres uma resposta, tira alguns segundos para ver se está certa. Uma resposta errada acrescenta tempo, por isso a melhor forma de ganhar é ser rápido e cuidadoso!";
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const imageUrl = q.image_path ? `${backendUrl}/api/files/${q.image_path}` : null;

  // Time bar: width = remaining %, color by elapsed thresholds (green <50%, yellow 50-80%, red >80%)
  const remainingPct = totalTime > 0 ? (remaining / totalTime) * 100 : 0;
  const elapsedPct = 100 - remainingPct;
  const barColor = elapsedPct < 50 ? "#2D9C5A" : elapsedPct < 80 ? "#FFC107" : "#E63946";

  // Time-up overlay (neutral / grey, no festive framing)
  if (timeUp) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-[#E5E7EB]" data-testid="quiz-time-up">
        <Clock size={64} className="text-[#6B7280] mb-4" />
        <h1 className="font-fredoka text-4xl md:text-5xl font-bold text-[#374151] mb-8" data-testid="quiz-time-up-message">
          {L("quiz_time_up_message") || (lang !== "en" ? "O tempo terminou!" : "Time's up!")}
        </h1>
        <button onClick={() => navigate("/")} className="px-8 py-3 rounded-full bg-[#4B5563] hover:bg-[#374151] text-white font-bold text-lg transition-colors" data-testid="quiz-time-up-home">
          {lang !== "en" ? "Voltar ao início" : "Back to home"}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden bg-[#FDFBF7] flex flex-col" data-testid="quiz-play-page">
      <div className="flex-1 min-h-0 w-full max-w-[1700px] mx-auto px-4 md:px-6 py-4 lg:py-6 grid grid-cols-1 quiz-play-grid gap-4 lg:gap-6 items-start lg:items-stretch"
        style={{ "--quiz-cols": `${colL}fr ${colC}fr ${colR}fr` }}>

        {/* LEFT (area 1): timer + question counter + exit */}
        <aside className={`flex flex-row lg:flex-col items-stretch lg:items-start justify-between gap-2 lg:gap-7 min-w-0 w-full ${sideVCenter ? "lg:justify-center" : "lg:justify-start lg:pt-6"}`} data-testid="quiz-sidebar">
          <div className="flex-1 lg:flex-none min-w-0 flex flex-col lg:flex-row items-center lg:items-center gap-0.5 lg:gap-2">
            <Clock size={26} strokeWidth={2} style={{ color: barColor }} className="shrink-0 lg:!w-[34px] lg:!h-[34px]" />
            <div className="text-center lg:text-left min-w-0">
              <div className="font-fredoka font-bold text-[#0B1D3A] leading-none text-2xl lg:text-4xl tabular-nums" data-testid="quiz-time-remaining">
                {Math.max(0, Math.ceil(remaining))}<span className="text-sm lg:text-xl font-semibold"> s</span>
              </div>
              <div className="font-nunito text-[9px] leading-tight lg:text-xs font-bold text-[#0B1D3A]/45 uppercase tracking-wide mt-0.5 lg:mt-1">
                {lang !== "en" ? "Tempo restante" : "Time left"}
              </div>
            </div>
          </div>

          <div className="hidden lg:block w-full h-2 rounded-full bg-[#0B1D3A]/10 overflow-hidden" data-testid="quiz-timer-bar">
            <div className="h-full rounded-full transition-all duration-200" style={{ width: `${remainingPct}%`, background: barColor }} data-testid="quiz-timer-bar-fill" />
          </div>

          {/* Penalties counter */}
          <div className="relative flex-1 lg:flex-none min-w-0 flex flex-col lg:flex-col items-center lg:items-start gap-0.5 lg:gap-1 lg:pt-3 lg:border-t lg:border-[#0B1D3A]/10 lg:w-full" data-testid="quiz-penalty-area">
            <div className="flex flex-col lg:flex-row items-center gap-0.5 lg:gap-1.5">
              <AlertTriangle size={20} className="shrink-0" style={{ color: "#E63946" }} />
              <span className="font-nunito text-[9px] leading-tight text-center lg:text-left lg:text-xs font-bold text-[#0B1D3A]/50 uppercase tracking-wide">{lang !== "en" ? "Penalizações" : "Penalties"}</span>
            </div>
            <div className="font-fredoka font-bold leading-none text-xl lg:text-3xl tabular-nums" style={{ color: penaltyTotal > 0 ? "#E63946" : "#0B1D3A" }} data-testid="quiz-penalty-total">
              +{penaltyTotal}<span className="text-xs lg:text-base font-semibold"> s</span>
            </div>
            {penaltyFlash && (
              <span key={penaltyFlash.key} className="quiz-penalty-flash absolute -top-1 left-1/2 lg:left-auto lg:right-2 -translate-x-1/2 lg:translate-x-0 font-fredoka font-extrabold text-xl" style={{ color: "#E63946" }} data-testid="quiz-penalty-flash">
                +{penaltyFlash.n}s
              </span>
            )}
          </div>

          <div className="flex-1 lg:flex-none min-w-0 flex flex-col lg:flex-col items-center lg:items-start gap-0.5 lg:gap-1 lg:pt-2 lg:border-t lg:border-[#0B1D3A]/10 lg:w-full">
            <div className="flex flex-col lg:flex-row items-center gap-0.5 lg:gap-2">
              <FileText size={20} className="text-[#2D9C5A] shrink-0 lg:mb-1" />
              <span className="font-nunito text-[9px] leading-tight lg:text-xs font-bold text-[#0B1D3A]/50 uppercase lg:normal-case tracking-wide">{t("quiz_question", "Pergunta")}</span>
            </div>
            <span className="font-fredoka font-bold text-[#0B1D3A] text-base lg:text-lg leading-none" data-testid="quiz-counter">
              {idx + 1} {t("quiz_of", "de")} {questions.length}
            </span>
          </div>

          <button
            onClick={() => setShowExit(true)}
            data-testid="quiz-exit-btn"
            title={L("quiz_exit_label") || (lang !== "en" ? "Sair" : "Exit")}
            className="shrink-0 self-center lg:self-auto lg:mt-2 inline-flex items-center justify-center gap-1.5 h-10 w-10 lg:h-auto lg:w-auto lg:px-4 lg:py-2 rounded-full bg-[#0B1D3A]/5 hover:bg-[#0B1D3A]/10 text-[#0B1D3A] font-bold text-sm transition-colors"
          >
            <LogOut size={16} /> <span className="hidden lg:inline">{L("quiz_exit_label") || (lang !== "en" ? "Sair" : "Exit")}</span>
          </button>
        </aside>

        {/* CENTER (11 units): question image — as large as possible */}
        <div className="w-full min-h-0 flex items-center justify-center lg:h-full" data-testid="quiz-image-col">
          {imageUrl ? (
            <div className="rounded-3xl overflow-hidden bg-white shadow-[0_12px_40px_-10px_rgba(11,29,58,0.15)] border border-[#0B1D3A]/5 flex items-center justify-center max-w-full lg:h-full"
              style={{ aspectRatio: fixedAspect ? imageAspect.replace("/", " / ") : undefined }} data-testid="quiz-question-image-wrap">
              <img src={imageUrl} alt="" loading="eager" decoding="sync"
                className={`bg-white ${fixedAspect ? "w-full h-full" : "max-w-full max-h-[78vh] w-auto h-auto"}`}
                style={{ objectFit: imageFit }} />
            </div>
          ) : null}
        </div>

        {/* RIGHT (area 3): question text + answer options (vertical) */}
        <div className={`w-full min-w-0 lg:h-full lg:overflow-y-auto lg:flex lg:flex-col ${sideVCenter ? "lg:justify-center" : ""}`} data-testid="quiz-answer-col">
          <h2 className="font-fredoka font-bold text-[#0B1D3A] mb-5 lg:mb-6 leading-snug text-center lg:text-left" style={{ fontSize: `${questionPx}px` }} data-testid="quiz-question-text">
            {questionText}
          </h2>

          <div className="flex flex-col gap-3 md:gap-4">
            {opts
              .map((opt, i) => ({ opt, i }))
              .filter(({ opt }) => opt && String(opt).trim())
              .map(({ opt, i }) => {
                const isWrong = wrongIdx.includes(i);
                const isCorrect = correctIdx === i;
                let vars = { "--btn-bg": answerPalette.bg, "--btn-dark": answerPalette.dark, "--btn-text": answerPalette.text };
                if (isCorrect) vars = { "--btn-bg": "#2D9C5A", "--btn-dark": "#1f6e40", "--btn-text": "#FFFFFF" };
                else if (isWrong) vars = { "--btn-bg": "#E63946", "--btn-dark": "#a82531", "--btn-text": "#FFFFFF" };
                return (
                  <button
                    key={i}
                    onClick={() => onAnswer(i)}
                    disabled={isWrong || correctIdx !== null}
                    data-testid={`quiz-answer-option-${i}`}
                    style={{ fontSize: `${answerPx}px`, ...vars }}
                    className={`home-btn fmt-${answerFormat} fx-${isCorrect || isWrong ? "none" : answerEffect} px-5 py-4 font-${answerFont} font-semibold ${isWrong ? "cursor-not-allowed opacity-90" : ""}`}
                  >
                    <span className="relative z-[1] inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-extrabold text-xl mr-3 shadow-sm ring-2 ring-black/5" style={{ background: "var(--btn-text)", color: "var(--btn-bg)" }}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="relative z-[1] self-stretch w-px mr-3 opacity-20" style={{ background: "var(--btn-text)" }} />
                    <span className="relative z-[1] text-left flex-1">{opt}</span>
                  </button>
                );
              })}
          </div>
        </div>
      </div>

      {/* Exit confirmation modal */}
      {showExit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1D3A]/60 backdrop-blur-sm px-6" data-testid="quiz-exit-modal">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
            <p className="font-fredoka text-2xl font-bold text-[#0B1D3A] mb-6" data-testid="quiz-exit-confirm-text">
              {L("quiz_exit_confirm") || (lang !== "en" ? "Tem a certeza que pretende desistir?" : "Are you sure you want to quit?")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowExit(false)}
                data-testid="quiz-exit-continue-btn"
                className="px-6 py-3 rounded-full bg-[#2D9C5A] text-white font-bold text-lg flex-1"
              >
                {L("quiz_exit_continue") || (lang !== "en" ? "Continuar" : "Continue")}
              </button>
              <button
                onClick={() => navigate("/")}
                data-testid="quiz-exit-quit-btn"
                className="px-6 py-3 rounded-full bg-[#E63946] text-white font-bold text-lg flex-1"
              >
                {L("quiz_exit_quit") || (lang !== "en" ? "Desistir" : "Give up")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tolerance window: slightly dim the screen but keep the quiz fully playable */}
      {grace && !timeUp && (
        <div className="fixed inset-0 z-30 pointer-events-none bg-[#0B1D3A]/25" data-testid="quiz-grace-overlay">
          <div className="absolute top-16 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-[#0B1D3A]/90 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg" data-testid="quiz-grace-banner">
            <Clock size={15} /> {lang !== "en" ? "Tempo extra — termina depressa!" : "Extra time — finish quickly!"}
          </div>
        </div>
      )}

      {/* Teacher-password gate (shown BEFORE the intro when a password is set) */}
      {phase === "password" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1D3A]/80 backdrop-blur-md px-5" data-testid="quiz-password-modal">
          <form
            onSubmit={verifyTeacherPassword}
            className="bg-white rounded-[28px] p-7 sm:p-9 max-w-md w-full text-center shadow-2xl"
          >
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#005F73] text-white mb-5 shadow-md">
              <LogOut size={30} strokeWidth={2.2} style={{ transform: "rotate(180deg)" }} />
            </span>
            <h1 className="font-fredoka text-2xl font-extrabold text-[#0B1D3A] mb-4" data-testid="quiz-password-prompt">
              {pwPrompt}
            </h1>
            <input
              type="password"
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              autoFocus
              autoComplete="off"
              className="w-full px-4 py-3 rounded-2xl border-2 border-[#0B1D3A]/15 focus:border-[#005F73] outline-none text-center text-lg mb-5"
              data-testid="quiz-password-input"
            />
            <button
              type="submit"
              disabled={pwChecking}
              className="w-full inline-flex items-center justify-center gap-2 px-9 py-4 rounded-2xl bg-[#005F73] text-white font-fredoka font-bold text-lg shadow-[0_10px_24px_-8px_rgba(0,95,115,0.6)] transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="quiz-password-submit"
            >
              {pwChecking ? (lang !== "en" ? "A verificar..." : "Checking...") : (lang !== "en" ? "Entrar" : "Enter")}
            </button>
          </form>
        </div>
      )}

      {/* Intro window with pedagogical message + Start button */}
      {phase === "intro" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1D3A]/70 backdrop-blur-md px-5" data-testid="quiz-intro-modal">
          <div className="bg-white rounded-[28px] p-7 sm:p-9 max-w-xl w-full text-center shadow-2xl">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1B5E3A] text-white mb-5 shadow-md">
              <Clock size={32} strokeWidth={2.2} />
            </span>
            <h1 className="font-fredoka text-2xl sm:text-3xl font-extrabold text-[#0B1D3A] mb-4" data-testid="quiz-intro-title">
              {L("quiz_intro_title") || (lang !== "en" ? "Antes de começares" : "Before you start")}
            </h1>
            <p className="font-nunito text-[#0B1D3A]/80 leading-relaxed text-base sm:text-lg mb-7 whitespace-pre-wrap" data-testid="quiz-intro-message">
              {L("quiz_intro_message") || introDefault}
            </p>
            <button onClick={startQuiz} data-testid="quiz-intro-start-btn" disabled={!imagesReady}
              className={`inline-flex items-center justify-center gap-2 px-9 py-4 rounded-2xl bg-[#1B5E3A] text-white font-fredoka font-bold text-lg shadow-[0_10px_24px_-8px_rgba(27,94,58,0.6)] transition-transform ${imagesReady ? "hover:bg-[#16492e] hover:-translate-y-0.5" : "opacity-60 cursor-not-allowed"}`}>
              {imagesReady
                ? (<><Play size={20} fill="currentColor" /> {L("quiz_intro_button") || (lang !== "en" ? "Começar" : "Start")}</>)
                : (<><span className="h-5 w-5 rounded-full border-2 border-white/40 border-t-white animate-spin" /> {(lang !== "en" ? "A preparar imagens" : "Preparing images")} {preloadPct}%</>)}
            </button>
          </div>
        </div>
      )}

      {/* Preparing gate (intro disabled): wait for image preload before timing */}
      {phase === "preparing" && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0B1D3A]/85 backdrop-blur-md px-6 text-center" data-testid="quiz-preparing-overlay">
          <span className="h-12 w-12 rounded-full border-4 border-white/30 border-t-white animate-spin mb-5" />
          <p className="font-fredoka text-2xl font-bold text-white" data-testid="quiz-preparing-text">
            {lang !== "en" ? "A preparar imagens" : "Preparing images"} {preloadPct}%
          </p>
          <p className="font-nunito text-white/70 text-sm mt-2 max-w-sm">
            {lang !== "en" ? "Para um tempo justo e igual para todos." : "For fair timing, equal for everyone."}
          </p>
        </div>
      )}

      {/* 3-2-1 countdown */}
      {phase === "countdown" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1D3A]/85 backdrop-blur-md" data-testid="quiz-countdown-overlay">
          <span key={countdownNum} className="quiz-countdown-num font-fredoka font-extrabold text-white" style={{ fontSize: "clamp(120px, 30vw, 280px)" }} data-testid="quiz-countdown-num">
            {countdownNum > 0 ? countdownNum : (lang !== "en" ? "Vai!" : "Go!")}
          </span>
        </div>
      )}
    </div>
  );
}
